import { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import axios from 'axios';
import {
  Graph,
  MagnifyingGlass,
  ArrowRight,
  Warning,
  User,
  CurrencyDollar,
  Bank
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${API_URL}/api` });
api.interceptors.request.use((config) => {
  try {
    const t = localStorage.getItem('access_token');
    if (t) config.headers.Authorization = `Bearer ${t}`;
  } catch (e) {
    console.warn('Token retrieval failed:', e);
  }
  return config;
});

export default function KnowledgeGraphPage() {
  const [graphData, setGraphData] = useState({ nodes: [], edges: [] });
  const [loading, setLoading] = useState(true);
  const [searchVendor, setSearchVendor] = useState('');
  const [suggestions, setSuggestions] = useState(null);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [hoveredNode, setHoveredNode] = useState(null);

  useEffect(() => {
    fetchGraph();
  }, []);

  const fetchGraph = async () => {
    setLoading(true);
    try {
      const response = await api.get('/knowledge-graph');
      setGraphData(response.data);
    } catch (error) {
      toast.error('Failed to load knowledge graph');
    } finally {
      setLoading(false);
    }
  };

  const handleSuggest = async () => {
    if (!searchVendor.trim()) return;
    setSuggestLoading(true);
    try {
      const response = await api.get('/knowledge-graph/suggest', { params: { vendor_name: searchVendor } });
      setSuggestions(response.data);
    } catch (error) {
      toast.error('Failed to get suggestions');
    } finally {
      setSuggestLoading(false);
    }
  };

  const vendorNodes = graphData.nodes.filter(n => n.type === 'vendor');
  const accountNodes = graphData.nodes.filter(n => n.type === 'account');

  // Simple force-layout positions for SVG visualization
  const svgWidth = 900;
  const svgHeight = 500;
  const vendorX = 200;
  const accountX = 700;
  const vendorSpacing = Math.min(60, (svgHeight - 80) / Math.max(vendorNodes.length, 1));
  const accountSpacing = Math.min(60, (svgHeight - 80) / Math.max(accountNodes.length, 1));
  const vendorStartY = (svgHeight - vendorNodes.length * vendorSpacing) / 2 + 20;
  const accountStartY = (svgHeight - accountNodes.length * accountSpacing) / 2 + 20;

  const nodePositions = {};
  vendorNodes.forEach((n, i) => {
    nodePositions[n.id] = { x: vendorX, y: vendorStartY + i * vendorSpacing };
  });
  accountNodes.forEach((n, i) => {
    nodePositions[n.id] = { x: accountX, y: accountStartY + i * accountSpacing };
  });

  if (loading) {
    return (
      <Layout title="Knowledge Graph">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-zinc-950 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Knowledge Graph">
      <div data-testid="knowledge-graph-page" className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-zinc-600">
              Vendor-account relationships learned from posted transactions. The graph shows how vendors connect to accounts, 
              helping the AI make smarter suggestions.
            </p>
          </div>
        </div>

        {/* Smart Suggestions */}
        <div className="bg-white border border-zinc-200 rounded-sm p-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <MagnifyingGlass size={18} weight="duotone" />
            Smart Account Suggestions
          </h3>
          <div className="flex gap-3 mb-4">
            <Input
              value={searchVendor}
              onChange={(e) => setSearchVendor(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSuggest()}
              placeholder="Enter vendor name to get account suggestions..."
              className="flex-1 rounded-sm"
              data-testid="vendor-suggest-input"
            />
            <Button onClick={handleSuggest} disabled={suggestLoading} className="bg-zinc-950 text-white rounded-sm" data-testid="suggest-button">
              {suggestLoading ? 'Searching...' : 'Get Suggestions'}
            </Button>
          </div>

          {suggestions && (
            <div className="bg-zinc-50 border border-zinc-200 p-4 rounded-sm" data-testid="suggestions-result">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium">Suggestions for "{suggestions.vendor_name}"</h4>
                {suggestions.is_sensitive && (
                  <span className="flex items-center gap-1 px-2 py-1 text-xs bg-amber-50 text-amber-700 border border-amber-200 rounded-sm">
                    <Warning size={12} /> Sensitive Vendor
                  </span>
                )}
              </div>

              {suggestions.default_account && (
                <div className="mb-3 p-3 bg-emerald-50 border border-emerald-200 rounded-sm">
                  <p className="text-xs uppercase tracking-widest text-emerald-600 mb-1">Vendor Default Account</p>
                  <p className="font-mono font-medium text-emerald-800">{suggestions.default_account}</p>
                </div>
              )}

              {suggestions.history_suggestions?.length > 0 ? (
                <div>
                  <p className="text-xs uppercase tracking-widest text-zinc-500 mb-2">From Transaction History</p>
                  <div className="space-y-2">
                    {suggestions.history_suggestions.map((s) => (
                      <div key={s.account_id} className="flex items-center justify-between py-2 px-3 bg-white border border-zinc-100 rounded-sm">
                        <div className="flex items-center gap-3">
                          <Bank size={16} className="text-zinc-400" />
                          <span className="font-mono text-sm font-medium">{s.account_id}</span>
                        </div>
                        <div className="flex items-center gap-4 text-xs text-zinc-500">
                          <span>Used {s.frequency}x</span>
                          <span>Avg: <span className="font-mono">${s.avg_amount.toLocaleString()}</span></span>
                          <span>Last: {s.last_used}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-zinc-500">No transaction history found for this vendor.</p>
              )}
            </div>
          )}
        </div>

        {/* Visual Network Graph */}
        <div className="bg-white border border-zinc-200 rounded-sm p-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Graph size={18} weight="duotone" />
            Vendor-Account Relationship Map
          </h3>

          {graphData.edges.length === 0 ? (
            <div className="text-center py-12 text-zinc-500">
              <Graph size={48} className="mx-auto mb-4 text-zinc-300" />
              <p className="font-medium mb-1">No relationships yet</p>
              <p className="text-sm">Post transactions with accounts assigned to build the knowledge graph.</p>
            </div>
          ) : (
            <div className="border border-zinc-100 rounded-sm overflow-hidden">
              <svg width="100%" viewBox={`0 0 ${svgWidth} ${svgHeight}`} className="bg-zinc-50" data-testid="graph-svg">
                {/* Column labels */}
                <text x={vendorX} y={20} textAnchor="middle" className="text-xs uppercase" fill="#71717A" style={{ fontSize: 11, letterSpacing: '0.1em' }}>Vendors</text>
                <text x={accountX} y={20} textAnchor="middle" className="text-xs uppercase" fill="#71717A" style={{ fontSize: 11, letterSpacing: '0.1em' }}>Accounts</text>

                {/* Edges */}
                {graphData.edges.map((edge) => {
                  const src = nodePositions[edge.source];
                  const tgt = nodePositions[edge.target];
                  if (!src || !tgt) return null;
                  const thickness = Math.min(4, Math.max(1, edge.count));
                  return (
                    <line
                      key={`${edge.source}__${edge.target}`}
                      x1={src.x + 80}
                      y1={src.y}
                      x2={tgt.x - 80}
                      y2={tgt.y}
                      stroke={hoveredNode && (hoveredNode === edge.source || hoveredNode === edge.target) ? '#09090B' : '#D4D4D8'}
                      strokeWidth={thickness}
                      opacity={hoveredNode ? (hoveredNode === edge.source || hoveredNode === edge.target ? 1 : 0.15) : 0.6}
                    />
                  );
                })}

                {/* Vendor nodes */}
                {vendorNodes.map((node) => {
                  const pos = nodePositions[node.id];
                  if (!pos) return null;
                  return (
                    <g
                      key={node.id}
                      onMouseEnter={() => setHoveredNode(node.id)}
                      onMouseLeave={() => setHoveredNode(null)}
                      style={{ cursor: 'pointer' }}
                    >
                      <rect x={pos.x - 80} y={pos.y - 16} width={160} height={32} rx={2} fill={node.is_sensitive ? '#FEF3C7' : '#F4F4F5'} stroke={node.is_sensitive ? '#F59E0B' : '#E4E4E7'} strokeWidth={1} />
                      {node.is_sensitive && <circle cx={pos.x - 68} cy={pos.y} r={4} fill="#F59E0B" />}
                      <text x={pos.x} y={pos.y + 4} textAnchor="middle" fill="#09090B" style={{ fontSize: 11, fontFamily: 'IBM Plex Sans' }}>
                        {node.label.length > 18 ? node.label.slice(0, 18) + '...' : node.label}
                      </text>
                    </g>
                  );
                })}

                {/* Account nodes */}
                {accountNodes.map((node) => {
                  const pos = nodePositions[node.id];
                  if (!pos) return null;
                  return (
                    <g
                      key={node.id}
                      onMouseEnter={() => setHoveredNode(node.id)}
                      onMouseLeave={() => setHoveredNode(null)}
                      style={{ cursor: 'pointer' }}
                    >
                      <rect x={pos.x - 80} y={pos.y - 16} width={160} height={32} rx={2} fill="#DBEAFE" stroke="#3B82F6" strokeWidth={1} />
                      <text x={pos.x} y={pos.y + 4} textAnchor="middle" fill="#1E3A5F" style={{ fontSize: 11, fontFamily: 'IBM Plex Mono' }}>
                        {node.label.length > 18 ? node.label.slice(0, 18) + '...' : node.label}
                      </text>
                    </g>
                  );
                })}
              </svg>
            </div>
          )}

          {/* Relationship Table */}
          {graphData.edges.length > 0 && (
            <div className="mt-4">
              <p className="text-xs uppercase tracking-widest text-zinc-500 mb-3">Relationship Details</p>
              <div className="overflow-x-auto">
                <table className="w-full table-dense">
                  <thead>
                    <tr className="text-left">
                      <th className="px-4 pt-3">Vendor</th>
                      <th className="px-4 pt-3">Account</th>
                      <th className="px-4 pt-3 text-right">Transactions</th>
                      <th className="px-4 pt-3 text-right">Total Amount</th>
                      <th className="px-4 pt-3">Last Used</th>
                    </tr>
                  </thead>
                  <tbody>
                    {graphData.edges.map((edge) => (
                      <tr key={`${edge.source}__${edge.target}`} className="hover:bg-zinc-50">
                        <td className="px-4 text-sm">{edge.source.replace('v_', '')}</td>
                        <td className="px-4 text-sm font-mono">{edge.target.replace('a_', '')}</td>
                        <td className="px-4 text-sm font-mono text-right">{edge.count}</td>
                        <td className="px-4 text-sm font-mono text-right">${edge.total_amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                        <td className="px-4 text-sm font-mono">{edge.last_date}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
